import React, { useState, useEffect, useCallback } from 'react';
import { Badge, LoadingState, PageHeader } from '../components/ui';
import './BoardPage.css';
import { formatDate } from '../utils/format';

interface BoardPost {
  post_id: string;
  title: string;
  content: string;
  author_name: string;
  category: 'general' | 'study' | 'code' | 'notice';
  created_at: string;
  view_count: number;
  is_pinned: boolean;
}

const PAGE_SIZE = 20;

const BoardPage: React.FC = () => {
  const [posts, setPosts] = useState<BoardPost[]>([]);
  const [selectedPost, setSelectedPost] = useState<BoardPost | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);

  const apiCall = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    return fetch(`${import.meta.env.VITE_API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });
  };

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        limit: PAGE_SIZE.toString(),
        offset: ((currentPage - 1) * PAGE_SIZE).toString()
      });

      if (selectedCategory !== 'all') {
        queryParams.append('category', selectedCategory);
      }

      const response = await apiCall(`/board/posts?${queryParams.toString()}`);
      if (response.ok) {
        const result = await response.json();
        setPosts(result.data?.posts ?? []);
        setTotalPosts(result.data.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Failed to load posts:', error);
    }
    setLoading(false);
  }, [currentPage, selectedCategory]);

  const loadPost = async (postId: string) => {
    try {
      const response = await apiCall(`/board/posts/${postId}`);
      if (response.ok) {
        const result = await response.json();
        setSelectedPost(result.data);
      }
    } catch (error) {
      console.error('Failed to load post:', error);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const getCategoryName = (category: string) => {
    const names = {
      all: '전체',
      general: '일반',
      study: '스터디',
      code: '코드공유',
      notice: '공지사항'
    };
    return names[category as keyof typeof names] || category;
  };

  const formatAuthorName = (authorName: string) => {
    if (!authorName) return '-';
    if (authorName.includes('@')) {
      const localPart = authorName.split('@')[0];
      return localPart || authorName;
    }
    return authorName;
  };

  const totalPages = Math.max(1, Math.ceil(totalPosts / PAGE_SIZE));
  const pageGroupStart = Math.floor((currentPage - 1) / 5) * 5 + 1;
  const pageGroupEnd = Math.min(totalPages, pageGroupStart + 4);
  const visiblePages = Array.from(
    { length: pageGroupEnd - pageGroupStart + 1 },
    (_, idx) => pageGroupStart + idx
  );

  if (selectedPost) {
    return (
      <div className="board-page">
        <div className="board-header">
          <button className="board-back-button" onClick={() => setSelectedPost(null)}>
            ← 목록으로
          </button>
          <h1>게시판</h1>
        </div>
        
        <div className="post-detail">
          <div className="post-header">
            <div className="post-category">{getCategoryName(selectedPost.category)}</div>
            {selectedPost.is_pinned && <Badge tone="info">고정</Badge>}
            <h2>{selectedPost.title}</h2>
            <div className="post-meta">
              <span>{formatAuthorName(selectedPost.author_name)}</span>
              <span>{formatDate(selectedPost.created_at)}</span>
              <span>조회 {selectedPost.view_count}</span>
            </div>
          </div>
          <div className="post-content">
            {selectedPost.content.split('\n').map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="board-page">
      <PageHeader title="게시판" description="공지사항과 AWS 자격증 학습 정보를 확인하세요." />

      <div className="board-categories">
        {['all', 'notice', 'study', 'code', 'general'].map(category => (
          <button
            key={category}
            className={`category-button ${selectedCategory === category ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category)}
          >
            {getCategoryName(category)}
          </button>
        ))}
      </div>

      {loading && <LoadingState label="게시글을 불러오는 중..." />}

      <div className="board-table-wrap">
        <table className="board-table">
          <thead>
            <tr>
              <th className="col-no">번호</th>
              <th className="col-category">분류</th>
              <th className="col-title">제목</th>
              <th className="col-author">작성자</th>
              <th className="col-date">작성일</th>
              <th className="col-views">조회</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((post, index) => {
              const rowNumber = totalPosts - ((currentPage - 1) * PAGE_SIZE + index);
              return (
                <tr
                  key={post.post_id}
                  className={`board-row ${post.is_pinned ? 'pinned' : ''}`}
                  onClick={() => loadPost(post.post_id)}
                >
                  <td>{post.is_pinned ? '공지' : rowNumber}</td>
                  <td>
                    <span className="post-category">{getCategoryName(post.category)}</span>
                  </td>
                  <td className="board-title-cell">
                    {post.is_pinned && <Badge tone="info" className="pin-icon">고정</Badge>}
                    <span className="board-title-text">{post.title}</span>
                  </td>
                  <td>{formatAuthorName(post.author_name)}</td>
                  <td>{formatDate(post.created_at)}</td>
                  <td>{post.view_count}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {posts.length === 0 && !loading && (
        <div className="board-empty">
          <p>게시글이 없습니다.</p>
        </div>
      )}

      {totalPosts > 0 && (
        <div className="board-pagination">
          <button
            className="page-btn"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1 || loading}
          >
            이전
          </button>
          {visiblePages.map((page) => (
            <button
              key={page}
              className={`page-btn ${page === currentPage ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
              disabled={loading}
            >
              {page}
            </button>
          ))}
          <button
            className="page-btn"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages || loading}
          >
            다음
          </button>
        </div>
      )}
    </div>
  );
};

export default BoardPage;
